import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { GraduationCap, Layers } from 'lucide-react';
import api from '../lib/api';
import { Spinner } from '../components/ui/Spinner';

const TOPIC_COLORS = {
  Psychology: '#15E402',
  'Risk Management': '#3BFF1E',
  'Tape Reading': '#16A80C',
  'Market Structure': '#84FF6E',
  Options: '#1E7A18',
  'Live Trading': '#CFFFC4',
  'Trading Business': '#10B981',
};

export default function Courses() {
  const navigate = useNavigate();
  const [courses, setCourses] = useState([]);
  const [topics, setTopics] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get('/courses').then((r) => r.data.courses || []).catch(() => []),
      api.get('/lessons').then((r) => r.data.lessons || []).catch(() => []),
    ])
      .then(([cs, lessons]) => {
        setCourses(cs);
        const map = {};
        lessons.forEach((l) => {
          const t = l.topic || 'General';
          if (!map[t]) map[t] = { topic: t, count: 0, completed: 0, thumbnail_url: l.thumbnail_url };
          map[t].count += 1;
          if (l.completed) map[t].completed += 1;
        });
        setTopics(Object.values(map).sort((a, b) => b.count - a.count));
      })
      .finally(() => setLoading(false));
  }, []);

  if (loading)
    return (
      <div className="grid h-[60vh] place-items-center">
        <Spinner className="h-8 w-8" />
      </div>
    );

  return (
    <div className="space-y-10">
      <div>
        <h1 className="font-heading text-3xl font-bold tracking-tight text-white">Courses</h1>
        <p className="mt-1 text-sm text-zinc-500">Structured courses — watch now.</p>
      </div>

      {courses.length > 0 ? (
        <section className="space-y-4">
          <h2 className="font-heading text-xl font-semibold text-white">Watch now</h2>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {courses.map((c, i) => (
              <motion.button
                key={c.course_id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.04 }}
                onClick={() => navigate(`/app/courses/${c.course_id}`)}
                data-testid={`course-card-${c.course_id}`}
                className="group overflow-hidden rounded-2xl border border-white/5 bg-ink-card text-left transition-colors hover:border-emerald/40"
              >
                <div className="relative aspect-video overflow-hidden bg-ink-elevated">
                  {c.cover_url ? (
                    <img src={c.cover_url} alt={c.name} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                  ) : (
                    <div className="grid h-full place-items-center text-zinc-600"><GraduationCap /></div>
                  )}
                </div>
                <div className="p-5">
                  <h3 className="font-heading font-semibold text-white">{c.name}</h3>
                  <p className="mt-1 line-clamp-2 text-sm text-zinc-400">{c.description}</p>
                  <div className="mt-3 tnum text-xs text-zinc-500">
                    {c.completed_count}/{c.lesson_count} lessons • {c.section_count} sections
                  </div>
                </div>
              </motion.button>
            ))}
          </div>
        </section>
      ) : (
        <div className="grain relative rounded-3xl border border-white/5 bg-ink-card p-16 text-center">
          <p className="font-heading text-lg text-white">No courses yet</p>
          <p className="mt-2 text-sm text-zinc-500">Published courses will appear here to watch.</p>
        </div>
      )}
    </div>
  );
}
